import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Database, Plus, RefreshCw } from "lucide-react";
import { useSettings } from "@/providers/SettingsProvider";
import { toast } from "@/hooks/use-toast";
import { generateDummyBelgianCompanies } from "@/utils/dummy-data/companies";
import { useTranslation } from "react-i18next";

interface CompaniesActionsProps {
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  fetchCompanies: () => Promise<void>;
  onAddCompany: () => void;
}

export const CompaniesActions = ({
  isGenerating,
  setIsGenerating,
  fetchCompanies,
  onAddCompany
}: CompaniesActionsProps) => {
  // Use try-catch to handle the case where SettingsProvider is not available
  let devMode = false;
  try {
    const { devMode: settingsDevMode } = useSettings();
    devMode = settingsDevMode;
  } catch (error) {
    console.error("SettingsProvider not available:", error);
    // Continue with default value for devMode
  }
  
  const { t } = useTranslation();
  
  const handleGenerateDummyData = async () => {
    if (isGenerating) return;
    
    try {
      setIsGenerating(true);
      toast({
        title: "Generating dummy Belgian companies",
        description: "This might take a few seconds...",
      });
      
      await generateDummyBelgianCompanies();
      
      toast({
        title: "Success",
        description: "Dummy Belgian companies generated successfully!",
      });
      
      // Refresh the companies list
      fetchCompanies();
    } catch (error) {
      console.error("Error generating dummy data:", error);
      toast({
        title: "Error",
        description: "Failed to generate dummy companies",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <div className="flex gap-2">
      {devMode && (
        <Button
          variant="outline"
          onClick={handleGenerateDummyData}
          disabled={isGenerating}
          className="flex items-center gap-2"
        >
          {isGenerating ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Database className="h-4 w-4" />
          )}
          <span>Generate Data</span>
        </Button>
      )}
      
      <Button onClick={onAddCompany} className="bg-digger-blue hover:bg-digger-blue-light">
        <Plus className="h-4 w-4 mr-2" />
        {t("companies.new")}
      </Button>
    </div>
  );
};
